import { MAX_ATTEMPTS, type ModePerformance, type TimedStats } from "@mk/shared";
import { useEffect, useRef, useState } from "react";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { useSession } from "../lib/session.ts";
import { exportPracticeArchive, importPracticeArchive, loadPracticePerformance } from "../features/solo/practiceHistory.ts";
import {
  averageAttemptsLabel, emptyPerformance, mergePerformance,
  performanceFromDistribution, rate,
} from "../features/stats/performance.ts";
import { messages } from "../i18n/index.ts";
import { formatNumber, formatPercent } from "../lib/edition.ts";
import { combinedDuelRecord } from "../features/stats/duelRecord.ts";
import { api } from "../lib/api.ts";
import { useOnline } from "../lib/pwa.ts";

const STAT_TABS = ["duel", "friendly", "daily", "team", "solo", "timed"] as const;
type StatTab = (typeof STAT_TABS)[number];
const TAB_KEY = "mk-stats-tab";

function storedTab(authed: boolean): StatTab {
  if (!authed) return "solo";
  try {
    const value = window.localStorage.getItem(TAB_KEY);
    if (STAT_TABS.includes(value as StatTab)) return value as StatTab;
  } catch {
    // Depolama kapalı.
  }
  return "duel";
}

interface StatisticsScreenProps {
  onBack: () => void;
  onSignIn: (reason: string) => void;
}

function PerformanceCards({ performance, rateLabel, rateValue }: {
  performance: ModePerformance; rateLabel: string; rateValue: string;
}) {
  return <div className="stat-grid">
    <div className="stat stat--win"><span className="stat__value">{performance.found}</span><span className="stat__label">{messages.profile.found}</span></div>
    <div className="stat stat--loss"><span className="stat__value">{performance.missed}</span><span className="stat__label">{messages.profile.missed}</span></div>
    <div className="stat"><span className="stat__value">{rateValue}</span><span className="stat__label">{rateLabel}</span></div>
    <div className="stat"><span className="stat__value">{averageAttemptsLabel(performance.averageAttempts)}</span><span className="stat__label">{messages.profile.averageAttempts}</span></div>
  </div>;
}

function AttemptDistribution({ distribution }: { distribution: Record<string, number> }) {
  const maxCount = Math.max(1, ...Object.values(distribution));
  return <>
    <h4 className="mt-4 mb-2 text-xs uppercase tracking-wider text-muted">{messages.profile.attemptDistribution}</h4>
    <div className="flex flex-col gap-1.5">{Array.from({ length: MAX_ATTEMPTS }, (_, index) => {
      const attempt = index + 1;
      const count = distribution[String(attempt)] ?? 0;
      return <div key={attempt} className="flex items-center gap-2">
        <span className="w-3 text-xs text-muted">{attempt}</span>
        <div className="dist-bar" data-empty={count === 0 ? "true" : undefined} style={{ width: `${Math.max(9, (count / maxCount) * 100)}%` }}>{count}</div>
      </div>;
    })}</div>
  </>;
}

export function StatisticsScreen({ onBack, onSignIn }: StatisticsScreenProps) {
  const { status, stats, performance } = useSession();
  const [practicePerformance, setPracticePerformance] = useState(loadPracticePerformance);
  const [importMessage, setImportMessage] = useState("");
  const [tab, setTab] = useState<StatTab>(() => storedTab(status === "authed"));
  function selectTab(next: StatTab) {
    setTab(next);
    try {
      window.localStorage.setItem(TAB_KEY, next);
    } catch {
      // Bellekte devam.
    }
  }
  const fileInput = useRef<HTMLInputElement>(null);
  const online = useOnline();
  const [timed, setTimed] = useState<TimedStats | null>(null);
  const [timedError, setTimedError] = useState(false);
  const [timedRetry, setTimedRetry] = useState(0);

  useEffect(() => {
    if (tab !== "timed" || status !== "authed" || !online) return;
    let cancelled = false;
    setTimedError(false);
    api<TimedStats>("/api/solo/timed/stats")
      .then((data) => { if (!cancelled) setTimed(data); })
      .catch(() => { if (!cancelled) setTimedError(true); });
    return () => { cancelled = true; };
  }, [online, status, tab, timedRetry]);

  function exportData() {
    const blob = new Blob([JSON.stringify(exportPracticeArchive())], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "muhtesem-kelime-tek-basina.json";
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  async function importData(file: File | undefined) {
    if (!file) return;
    try {
      if (file.size > 1_000_000) throw new Error("archive_too_large");
      importPracticeArchive(await file.text());
      setPracticePerformance(loadPracticePerformance());
      setImportMessage(messages.profile.importDone);
    } catch {
      setImportMessage(messages.profile.importFailed);
    }
    if (fileInput.current) fileInput.current.value = "";
  }
  const competitivePerformance = performance?.competitive ?? emptyPerformance();
  const friendlyPerformance = performance?.friendly ?? emptyPerformance();
  const allDuelPerformance = mergePerformance(competitivePerformance, friendlyPerformance);
  const dailyPerformance = performanceFromDistribution(stats?.soloPlayed ?? 0, stats?.soloWon ?? 0, stats?.guessDistribution ?? {});
  const { wins: duelWins, losses: duelLosses, draws: duelDraws, total: duelTotal } = combinedDuelRecord(stats);
  const friendlyWins = stats?.friendlyDuelWins ?? 0;
  const friendlyTotal = friendlyWins + (stats?.friendlyDuelLosses ?? 0) + (stats?.friendlyDuelDraws ?? 0);
  const teamWins = stats?.teamDuelWins ?? 0;
  const teamLosses = stats?.teamDuelLosses ?? 0;
  const teamDraws = stats?.teamDuelDraws ?? 0;
  const teamTotal = teamWins + teamLosses + teamDraws;
  const teamWords = stats?.teamWordsSolved ?? 0;
  const teamRounds = stats?.teamRoundsCompleted ?? 0;
  const teamAttempts = stats?.teamCompletedAttempts ?? 0;

  const authed = status === "authed";
  const empty = (text: string) => <p className="history-gate text-center text-sm text-muted">{text}</p>;
  const signInGate = <section className="flex flex-col gap-2">
    <p className="text-sm text-muted">{messages.profile.accountRequired}</p>
    <button className="btn-secondary" onClick={() => onSignIn(messages.profile.accountRequired)}>{messages.profile.signIn}</button>
  </section>;

  return <div className="app">
    <ScreenHeader title={messages.profile.statistics} onBack={onBack} />
    <main className="overflow-y-auto px-4 py-5"><div className="mx-auto flex w-full max-w-md flex-col gap-5">
      <div className="history-tabs" role="tablist" aria-label={messages.profile.statistics}>
        {STAT_TABS.map((item) => (
          <button key={item} type="button" role="tab" aria-selected={tab === item} className="history-tab" onClick={() => selectTab(item)}>
            {messages.profile.statTabs[item]}
          </button>
        ))}
      </div>

      <div role="tabpanel" aria-label={messages.profile.statTabs[tab]}>
        {tab !== "solo" && !authed && signInGate}

        {tab === "duel" && authed && (duelTotal === 0 ? empty(messages.profile.emptyStats.duel) : <section>
          <div className="stat-grid">
            <div className="stat stat--win"><span className="stat__value">{duelWins}</span><span className="stat__label">{messages.profile.wins}</span></div>
            <div className="stat stat--loss"><span className="stat__value">{duelLosses}</span><span className="stat__label">{messages.profile.losses}</span></div>
            <div className="stat"><span className="stat__value">{duelDraws}</span><span className="stat__label">{messages.profile.draw}</span></div>
            <div className="stat"><span className="stat__value">{formatPercent(duelWins / duelTotal)}</span><span className="stat__label">{messages.profile.winRate}</span></div>
          </div>
          <h4 className="mt-4 mb-2 text-xs uppercase tracking-wider text-muted">{messages.profile.wordPerformance}</h4>
          <PerformanceCards performance={allDuelPerformance} rateLabel={messages.profile.findRate} rateValue={rate(allDuelPerformance.found, allDuelPerformance.played)} />
          <AttemptDistribution distribution={allDuelPerformance.guessDistribution} />
        </section>)}

        {tab === "friendly" && authed && (friendlyTotal === 0 ? empty(messages.profile.emptyStats.friendly) : <section>
          <PerformanceCards performance={friendlyPerformance} rateLabel={messages.profile.winRate} rateValue={rate(friendlyWins, friendlyTotal)} />
          <p className="mt-2 text-center text-xs text-muted">{messages.friendsScreen.record(friendlyWins, stats?.friendlyDuelLosses ?? 0, stats?.friendlyDuelDraws ?? 0)}</p>
          <AttemptDistribution distribution={friendlyPerformance.guessDistribution} />
        </section>)}

        {tab === "daily" && authed && (dailyPerformance.played === 0 ? empty(messages.profile.emptyStats.daily) : <section>
          <PerformanceCards performance={dailyPerformance} rateLabel={messages.profile.findRate} rateValue={rate(dailyPerformance.found, dailyPerformance.played)} />
          <div className="stat-grid stat-grid--two mt-2">
            <div className="stat stat--streak"><span className="stat__value">{stats?.currentStreak ?? 0}</span><span className="stat__label">{messages.profile.currentStreak}</span></div>
            <div className="stat stat--streak"><span className="stat__value">{stats?.maxStreak ?? 0}</span><span className="stat__label">{messages.profile.longestStreak}</span></div>
          </div>
          <AttemptDistribution distribution={dailyPerformance.guessDistribution} />
        </section>)}

        {tab === "team" && authed && (teamTotal === 0 ? empty(messages.profile.emptyStats.team) : <section>
          <div className="stat-grid">
            <div className="stat stat--win"><span className="stat__value">{teamWins}</span><span className="stat__label">{messages.profile.wins}</span></div>
            <div className="stat stat--loss"><span className="stat__value">{teamLosses}</span><span className="stat__label">{messages.profile.losses}</span></div>
            <div className="stat"><span className="stat__value">{teamDraws}</span><span className="stat__label">{messages.profile.draw}</span></div>
            <div className="stat"><span className="stat__value">{formatPercent(teamWins / teamTotal)}</span><span className="stat__label">{messages.profile.winRate}</span></div>
            <div className="stat"><span className="stat__value">{teamWords}</span><span className="stat__label">{messages.profile.teamWords}</span></div>
            <div className="stat"><span className="stat__value">{teamRounds === 0 ? "—" : (teamAttempts / teamRounds).toFixed(1)}</span><span className="stat__label">{messages.profile.teamAverage}</span></div>
          </div>
        </section>)}

        {tab === "timed" && authed && !online && timed === null && empty(messages.duelHistory.connectionRequired)}
        {tab === "timed" && authed && online && timedError && timed === null && <section className="flex flex-col gap-2">
          <p className="text-sm text-muted">{messages.profile.loadFailed}</p>
          <button type="button" className="btn-secondary" onClick={() => setTimedRetry((value) => value + 1)}>{messages.common.tryAgain}</button>
        </section>}
        {tab === "timed" && authed && online && !timedError && timed === null && <p className="text-sm text-muted">{messages.common.loading}</p>}
        {tab === "timed" && authed && timed && (timed.played === 0 ? empty(messages.profile.emptyStats.timed) : <section>
          <div className="stat-grid">
            <div className="stat"><span className="stat__value">{timed.played}</span><span className="stat__label">{messages.profile.timedRuns}</span></div>
            <div className="stat stat--win"><span className="stat__value">{timed.best?.wordsSolved ?? 0}</span><span className="stat__label">{messages.profile.timedBest}</span></div>
            <div className="stat"><span className="stat__value">{timed.averageWords === null ? "—" : formatNumber(timed.averageWords, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span><span className="stat__label">{messages.profile.timedAverageWords}</span></div>
            <div className="stat"><span className="stat__value">{timed.totalWords}</span><span className="stat__label">{messages.profile.timedTotalWords}</span></div>
          </div>
          <h4 className="mt-4 mb-2 text-xs uppercase tracking-wider text-muted">{messages.profile.timedWordPerformance}</h4>
          <PerformanceCards performance={timed.words} rateLabel={messages.profile.findRate} rateValue={rate(timed.words.found, timed.words.played)} />
          <AttemptDistribution distribution={timed.words.guessDistribution} />
        </section>)}

        {tab === "solo" && <section>
          {practicePerformance.played === 0 ? empty(messages.profile.emptyStats.solo) : <>
            <PerformanceCards performance={practicePerformance} rateLabel={messages.profile.findRate} rateValue={rate(practicePerformance.found, practicePerformance.played)} />
            <p className="mt-2 text-center text-xs text-muted">{messages.profile.localGames}</p>
            <AttemptDistribution distribution={practicePerformance.guessDistribution} />
          </>}
          <div className="mt-4 flex flex-col gap-2">
            <button type="button" className="btn-secondary" onClick={exportData}>{messages.profile.exportData}</button>
            <button type="button" className="btn-secondary" onClick={() => fileInput.current?.click()}>{messages.profile.importData}</button>
            <input ref={fileInput} className="hidden" type="file" accept="application/json,.json" aria-label={messages.profile.importData} onChange={(event) => { void importData(event.target.files?.[0]); }} />
            {importMessage && <p role="status" className="text-sm text-muted">{importMessage}</p>}
          </div>
        </section>}
      </div>
    </div></main>
  </div>;
}
